import React, { useEffect, useRef, useState } from "react";
import { Loader2, Send, Sparkles, Plus, MessageSquare } from "lucide-react";
import api from "../lib/api";
import { useAsistenteChat } from "../lib/useAsistenteChat";

const EJEMPLOS = [
  "¿Qué empleado tiene mejor ratio de ventas mixtas este mes?",
  "¿Cómo ha evolucionado el ticket medio en los últimos meses?",
  "¿Qué productos parados tienen más valor en stock?",
  "¿Qué laboratorio concentra más stock y cuánto vende?",
];

function Bubble({ m }) {
  const user = m.role === "user";
  return (
    <div className={`flex ${user ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.role}`}>
      <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap leading-relaxed ${user ? "bg-slate-900 text-white rounded-br-sm" : "bg-white border border-slate-200 rounded-bl-sm"}`}>
        {m.content || <Loader2 size={14} className="animate-spin text-slate-400" />}
      </div>
    </div>
  );
}

export default function Asistente() {
  const [sessions, setSessions] = useState([]);
  const [text, setText] = useState("");
  const endRef = useRef(null);
  const loadSessions = () => api.get("/asistente/sessions").then((r) => setSessions(r.data)).catch(() => {});
  const { sid, setSid, msgs, setMsgs, busy, send: sendMsg, reset } = useAsistenteChat(loadSessions);

  useEffect(() => { loadSessions(); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [msgs]);

  const open = async (id) => {
    setSid(id);
    const r = await api.get(`/asistente/sessions/${id}`);
    setMsgs(r.data);
  };

  const send = (q) => {
    const message = (q ?? text).trim();
    if (!message || busy) return;
    setText("");
    sendMsg(message);
  };

  return (
    <div className="p-8 fade-in h-full" data-testid="asistente-page">
      <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Inteligencia</div>
      <h1 className="font-display text-4xl font-extrabold mt-1 tracking-tight flex items-center gap-3"><Sparkles size={28} /> Asistente IA</h1>
      <p className="text-slate-600 mt-1 text-sm mb-6">Pregunta en español sobre tus ventas, tu stock y tus empleados. Solo usa los datos de tu farmacia.</p>
      <div className="grid lg:grid-cols-[240px_1fr] gap-6">
        <aside className="card p-3 h-fit" data-testid="asistente-sessions">
          <button onClick={reset} disabled={busy} className="btn btn-outline w-full mb-2" data-testid="asistente-new-chat"><Plus size={14} /> Nueva conversación</button>
          {sessions.map((s) => (
            <button key={s.session_id} onClick={() => open(s.session_id)} disabled={busy} data-testid={`asistente-session-${s.session_id}`}
                    className={`w-full text-left text-xs px-2 py-2 rounded-md flex gap-2 transition-colors ${sid === s.session_id ? "bg-slate-100 font-semibold" : "hover:bg-slate-50"}`}>
              <MessageSquare size={13} className="shrink-0 mt-0.5 text-slate-400" /><span className="line-clamp-2">{s.titulo}</span>
            </button>
          ))}
        </aside>
        <div className="card flex flex-col min-h-[520px]">
          <div className="flex-1 p-5 space-y-3 overflow-y-auto max-h-[60vh] bg-slate-50/60" data-testid="asistente-messages">
            {!msgs.length && (
              <div className="grid sm:grid-cols-2 gap-2" data-testid="asistente-ejemplos">
                {EJEMPLOS.map((e) => (
                  <button key={e} onClick={() => send(e)} data-testid={`asistente-ejemplo-${EJEMPLOS.indexOf(e)}`} className="text-left text-sm card p-3 hover:border-slate-400 transition-colors">{e}</button>
                ))}
              </div>
            )}
            {msgs.map((m, i) => <Bubble key={i} m={m} />)}
            <div ref={endRef} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-slate-200 p-3 flex gap-2">
            <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Escribe tu pregunta…" className="input flex-1" data-testid="asistente-input" />
            <button type="submit" disabled={busy || !text.trim()} className="btn btn-primary" data-testid="asistente-send">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Enviar
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
